import lzString from 'lz-string/libs/lz-string'
import md5 from 'md5'

/*
plan.hydrate_version === 2 indicates the plan uses TEA codes rather than database IDs for courses.
This is a placeholder until it can be safely determined to obsolete the version 1 bookmarked plans based on database IDs.
 */

export const makePlanHashId = (plan) => {
  var hash = md5(plan)
  return hash
}

const sortPlanCoursesMinimal = (courseMap) => {
  return courseMap.sort((a, b) => {
    if (a < b) { return -1 }
    if (a > b) { return 1 }
    return 0
  })
}

export const dehydratePlan = plan => {
  if (plan.hydrate_version === 2) {
    return {
      ...plan,
      coreCourses: plan.coreCourses.map(c => c.course_id),
      requiredCourses: plan.requiredCourses.map(c => c.course_id),
      electiveCourses: plan.electiveCourses.map(c => c.course_id),
      subStates: {}
    }
  } else {
    return {
      ...plan,
      coreCourses: plan.coreCourses.map(c => c.id),
      requiredCourses: plan.requiredCourses.map(c => c.id),
      electiveCourses: plan.electiveCourses.map(c => c.id),
      subStates: {}
    }
  }
}

export const validatePlan = (plan, courseList) => {
  let courseIdList
  if (plan.hydrate_version === 2) {
    courseIdList = courseList.map(courseItem => { return courseItem.course_id })
  } else {
    courseIdList = courseList.map(courseItem => { return courseItem.id })
  }
  const validCoreCourses = plan.coreCourses.filter((cid) => courseIdList.indexOf(cid) >= 0)
  if (validCoreCourses.length !== plan.coreCourses.length) {
    return false
  }
  const validElectiveCourses = plan.electiveCourses.filter((cid) => courseIdList.indexOf(cid) >= 0)
  if (validElectiveCourses.length !== plan.electiveCourses.length) {
    return false
  }
  const validRequiredCourses = plan.requiredCourses.filter((cid) => courseIdList.indexOf(cid) >= 0)
  if (validRequiredCourses.length !== plan.requiredCourses.length) {
    return false
  }
  return true
}

export const hydratePlan = (plan, courseList) => {
  let courseMap = {}
  if (plan.hydrate_version === 2) {
    courseMap = courseList.reduce((map, c) => {
      map[c.course_id] = c
      return map
    }, {})
  } else {
    courseMap = courseList.reduce((map, c) => {
      map[c.id] = c
      return map
    }, {})
  }
  plan = {
    ...plan,
    allowBack: false,
    coreCourses: plan.coreCourses.map(cid => courseMap[cid]),
    requiredCourses: plan.requiredCourses.map(cid => courseMap[cid]),
    electiveCourses: plan.electiveCourses.map(cid => courseMap[cid])
  }
  return plan
}

export const hydratePlanWithMinimalForHash = (plan, courseList) => {
  let courseMap = {}
  if (plan.hydrate_version === 2) {
    courseMap = courseList.reduce((map, c) => {
      map[c.course_id] = c.id
      return map
    }, {})
  } else {
    courseMap = courseList.reduce((map, c) => {
      map[c.id] = c.course_id
      return map
    }, {})
  }
  plan = {
    school_code: plan.school.code,
    endorsement: plan.endorsement,
    path: plan.careerPath,
    coreCourses: sortPlanCoursesMinimal(plan.coreCourses.map(cid => {
      if (courseMap[cid]) {
        return courseMap[cid]
      } else {
        return 'notfound'
      }
    })),
    requiredCourses: sortPlanCoursesMinimal(plan.requiredCourses.map(cid => {
      if (courseMap[cid]) {
        return courseMap[cid]
      } else {
        return 'notfound'
      }
    })),
    electiveCourses: sortPlanCoursesMinimal(plan.electiveCourses.map(cid => {
      if (courseMap[cid]) {
        return courseMap[cid]
      } else {
        return 'notfound'
      }
    }))
  }
  return plan
}

export const hydratePlanWithTeaCodes = (plan, courseList) => {
  const courseNotFoundMap = {
    course_id: 'notfound',
    course_title: 'notfound',
    subject_area_code: '',
    subject_code: ''
  }
  let courseMap = {}
  if (plan.hydrate_version === 2) {
    courseMap = courseList.reduce((map, c) => {
      map[c.course_id] = {
        db_course_id: c.id,
        course_id: c.course_id,
        course_title: c.course_title,
        subject_area_code: c.subject_area_code,
        subject_code: c.subject_code
      }
      return map
    }, {})
  } else {
    courseMap = courseList.reduce((map, c) => {
      map[c.id] = {
        db_course_id: c.id,
        course_id: c.course_id,
        course_title: c.course_title,
        subject_area_code: c.subject_area_code,
        subject_code: c.subject_code
      }
      return map
    }, {})
  }
  plan = {
    school_db_id: plan.school.id,
    school_code: plan.school.code,
    school_name: plan.school.name,
    is_school_charter: plan.school.is_charter,
    is_school_active: plan.school.is_school_active,
    is_school_suspend: plan.school.is_suspend,
    school_mou: plan.school.mou,
    school_mou_last_updated: plan.school.mou_last_updated,
    endorsement: plan.endorsement,
    path: plan.careerPath,
    allowBack: false,
    coreCourses: plan.coreCourses.map(cid => {
      if (courseMap[cid]) {
        return courseMap[cid]
      } else {
        return Object.assign({}, {
          ...courseNotFoundMap,
          'notfound_course_id': cid
        })
      }
    }),
    requiredCourses: plan.requiredCourses.map(cid => {
      if (courseMap[cid]) {
        return courseMap[cid]
      } else {
        return Object.assign({}, {
          ...courseNotFoundMap,
          'notfound_course_id': cid
        })
      }
    }),
    electiveCourses: plan.electiveCourses.map(cid => {
      if (courseMap[cid]) {
        return courseMap[cid]
      } else {
        return Object.assign({}, {
          ...courseNotFoundMap,
          'notfound_course_id': cid
        })
      }
    })
  }
  return plan
}

export const compressPlan = plan =>
  lzString.compressToEncodedURIComponent(JSON.stringify(plan))
export const decompressPlan = compressedPlan =>
  JSON.parse(lzString.decompressFromEncodedURIComponent(compressedPlan))



// WEBPACK FOOTER //
// ./src/services/compressPlan.js